import { useState, useMemo } from 'react';
import { Play, Download, Trash2, Loader2, ChevronDown, ChevronRight, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { taskApi, documentApi, patentApi } from '@/services/api';
import { useNotificationStore } from '@/stores';
import type { TemplateItem, FilledTemplateItem, SelectedDocInfo } from '@/components/TemplatesPage';

interface GenerateTemplateControlProps {
  selectedTemplate: TemplateItem | null;
  selectedOfficeAction: SelectedDocInfo | null;
  selectedClaims: SelectedDocInfo | null;
  selectedSupplementalDocs: SelectedDocInfo[];
  uploadedClaims: SelectedDocInfo | null;
  uploadedOA: SelectedDocInfo | null;
  selectedAppNo: string | null;
  filledTemplates: FilledTemplateItem[];
  filledTemplatesLoading: boolean;
  fetchFilledTemplates: () => Promise<void>;
}

function formatDate(value: string): string {
  // Append Z if no timezone info so the string is parsed as UTC, not local time
  const normalized = /Z|[+-]\d{2}:\d{2}$/.test(value) ? value : value + 'Z';
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export function GenerateTemplateControl({
  selectedTemplate,
  selectedOfficeAction,
  selectedClaims,
  selectedSupplementalDocs,
  uploadedClaims,
  uploadedOA,
  selectedAppNo,
  filledTemplates,
  filledTemplatesLoading,
  fetchFilledTemplates,
}: GenerateTemplateControlProps) {
  const { addNotification } = useNotificationStore();
  const [generating, setGenerating] = useState(false);
  const [generateStatus, setGenerateStatus] = useState('');
  const [filledListOpen, setFilledListOpen] = useState(true);
  const [detailDoc, setDetailDoc] = useState<FilledTemplateItem | null>(null);
  const [downloadingProsDoc, setDownloadingProsDoc] = useState<string | null>(null);

  // Sort filled templates descending by created_date (newest first)
  const sortedFilledTemplates = useMemo(() => {
    const hasAnyDate = filledTemplates.some((t) => t.created_date);
    if (hasAnyDate) {
      return [...filledTemplates].sort((a, b) => {
        const dateA = a.created_date ? new Date(a.created_date).getTime() : 0;
        const dateB = b.created_date ? new Date(b.created_date).getTime() : 0;
        return dateB - dateA;
      });
    }
    // No date fields — reverse to put newest (last from API) at top
    return [...filledTemplates].reverse();
  }, [filledTemplates]);

  // Can generate if: template selected AND (app with OA/claims OR uploaded OA)
  const canGenerate =
    selectedTemplate &&
    (
      (selectedAppNo && (selectedOfficeAction || selectedClaims || uploadedClaims)) ||
      uploadedOA
    );

  const handleGenerate = async () => {
    if (!selectedTemplate) return;

    try {
      setGenerating(true);
      setGenerateStatus('Starting generation...');

      const response = await taskApi.generateFilledTemplate(
        selectedTemplate.template_id,
        selectedTemplate.template_name,
        selectedAppNo,
        selectedOfficeAction?.docId || null,
        selectedClaims?.docId || null,
        selectedSupplementalDocs.map((d) => d.docId),
        uploadedClaims?.uploadId || uploadedClaims?.docId || null,
        uploadedOA?.uploadId || uploadedOA?.docId || null,
        uploadedClaims?.filename || null,
        uploadedOA?.filename || null
      );

      if (response?.job_id) {
        setGenerateStatus('Generating document...');

        taskApi.startGraduatedPolling(
          async () => {
            const result = await documentApi.getJobStatus(response.job_id);
            if (result?.status?.toLowerCase().startsWith('complete')) return result;
            if (result?.status?.toLowerCase().includes('error')) return result;
            return null;
          },
          async (result) => {
            if (result?.status?.toLowerCase().startsWith('complete')) {
              addNotification({ type: 'success', title: 'Generated', message: 'Filled template generated successfully.' });
              await fetchFilledTemplates();
              setFilledListOpen(true);
            } else {
              addNotification({ type: 'error', title: 'Error', message: `Generation failed: ${result?.status || 'Unknown error'}` });
            }
            setGenerating(false);
            setGenerateStatus('');
          },
          (error) => {
            console.error('Generation polling error:', error);
            addNotification({ type: 'error', title: 'Error', message: 'Template generation failed.' });
            setGenerating(false);
            setGenerateStatus('');
          },
          (_attempts, elapsedTime) => {
            setGenerateStatus(`Generating... (${Math.floor(elapsedTime / 1000)}s)`);
          }
        );
      } else {
        // No job_id - maybe immediate completion
        addNotification({ type: 'success', title: 'Generated', message: 'Filled template generated.' });
        await fetchFilledTemplates();
        setFilledListOpen(true);
        setGenerating(false);
        setGenerateStatus('');
      }
    } catch (error) {
      console.error('Generation error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to generate filled template.' });
      setGenerating(false);
      setGenerateStatus('');
    }
  };

  const handleDownloadFilled = async (doc: FilledTemplateItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const response = await taskApi.getFilledTemplate(doc.filled_template_id, doc.filled_template_name);
      if (!response?.data) throw new Error('Invalid response');
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = doc.filled_template_name.split('/').pop() || 'filled_template.docx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Download error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to download filled template.' });
    }
  };

  const handleDeleteFilled = async (doc: FilledTemplateItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await taskApi.deleteFilledTemplate(doc.filled_template_id);
      addNotification({ type: 'success', title: 'Deleted', message: 'Filled template deleted.' });
      if (detailDoc?.filled_template_id === doc.filled_template_id) {
        setDetailDoc(null);
      }
      await fetchFilledTemplates();
    } catch (error) {
      console.error('Delete error:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to delete filled template.' });
    }
  };

  const handleDownloadProsDoc = async (appNo: string, docId: string, label: string) => {
    const key = `${appNo}-${docId}`;
    if (downloadingProsDoc === key) return;
    setDownloadingProsDoc(key);
    try {
      const result = await patentApi.getProsecutionDocUrl(appNo, docId, true);
      const url = result?.url || result?.download_url;
      if (url) {
        window.open(url, '_blank');
      } else {
        throw new Error('No download URL returned');
      }
    } catch (err: any) {
      addNotification({ type: 'error', title: `${label} download failed`, message: err.message || 'Failed to download PDF' });
    } finally {
      setDownloadingProsDoc(null);
    }
  };

  const handleDownloadUploadedDoc = async (uploadId: string, filename: string, label: string) => {
    const key = `uploaded-${uploadId}`;
    if (downloadingProsDoc === key) return;
    setDownloadingProsDoc(key);
    try {
      const response = await taskApi.downloadUploadedDoc(uploadId, filename);
      if (!response?.data) throw new Error('No data returned');
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err: any) {
      addNotification({ type: 'error', title: `${label} download failed`, message: err.message || 'Failed to download file' });
    } finally {
      setDownloadingProsDoc(null);
    }
  };

  const detailDisplayName = detailDoc?.filled_template_name?.split('/').pop() || detailDoc?.filled_template_name || '';

  return (
    <div className="p-3 space-y-3">
      {/* Generate Button */}
      <Button
        className="w-full gap-1.5 bg-teal-600 hover:bg-teal-700"
        onClick={handleGenerate}
        disabled={!canGenerate || generating}
      >
        {generating ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {generateStatus || 'Generating...'}
          </>
        ) : (
          <>
            <Play className="w-4 h-4" />
            Generate Filled Template
          </>
        )}
      </Button>

      {!canGenerate && !generating && (
        <p className="text-xs text-slate-400 text-center">
          Select a template and either: upload an OA, or select an application with at least one document.
        </p>
      )}

      {/* Filled Templates List */}
      <Collapsible open={filledListOpen} onOpenChange={setFilledListOpen}>
        <CollapsibleTrigger className="flex items-center gap-1 w-full text-xs font-medium text-slate-600 hover:text-slate-800 py-1">
          {filledListOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Generated Documents ({filledTemplates.length})
        </CollapsibleTrigger>
        <CollapsibleContent>
          {filledTemplatesLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              <span className="ml-2 text-xs text-slate-400">Loading...</span>
            </div>
          ) : sortedFilledTemplates.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-3">No generated documents yet.</p>
          ) : (
            <div className="overflow-y-auto max-h-[250px]">
              <div className="space-y-1">
                {sortedFilledTemplates.map((doc) => {
                  const displayName = doc.filled_template_name.split('/').pop() || doc.filled_template_name;
                  const dateStr = doc.created_date ? formatDate(doc.created_date) : '';
                  return (
                    <div
                      key={doc.filled_template_id}
                      className="group flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-xs transition-colors"
                      onClick={() => setDetailDoc(doc)}
                    >
                      <FileCheck className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="block truncate text-slate-700" title={displayName}>{displayName}</span>
                        {dateStr && <span className="text-[10px] text-slate-400">{dateStr}</span>}
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => handleDownloadFilled(doc, e)}
                          title="Download"
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => handleDeleteFilled(doc, e)}
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Detail Modal */}
      <Dialog open={!!detailDoc} onOpenChange={(open) => { if (!open) setDetailDoc(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base truncate pr-6" title={detailDisplayName}>
              {detailDisplayName}
            </DialogTitle>
            {detailDoc?.created_date && (
              <DialogDescription className="text-xs text-slate-500">
                {formatDate(detailDoc.created_date)}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-2 py-1">
            {/* Generated document download */}
            <Button
              className="w-full gap-2 justify-start"
              onClick={() => detailDoc && handleDownloadFilled(detailDoc)}
            >
              <Download className="w-4 h-4 flex-shrink-0" />
              Download Generated Document
            </Button>

            {/* Office action download — uploaded or from prosecution history */}
            {(detailDoc?.uploaded_oa_id && detailDoc?.uploaded_oa_filename) ? (
              <Button
                variant="outline"
                className="w-full gap-2 justify-start"
                onClick={() => handleDownloadUploadedDoc(detailDoc.uploaded_oa_id, detailDoc.uploaded_oa_filename, 'Office action')}
                disabled={downloadingProsDoc === `uploaded-${detailDoc.uploaded_oa_id}`}
              >
                {downloadingProsDoc === `uploaded-${detailDoc.uploaded_oa_id}` ? (
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 flex-shrink-0" />
                )}
                Download Office Action
              </Button>
            ) : detailDoc?.app_no && detailDoc?.doc_id ? (
              <Button
                variant="outline"
                className="w-full gap-2 justify-start"
                onClick={() => handleDownloadProsDoc(detailDoc.app_no, detailDoc.doc_id, 'Office action')}
                disabled={downloadingProsDoc === `${detailDoc.app_no}-${detailDoc.doc_id}`}
              >
                {downloadingProsDoc === `${detailDoc.app_no}-${detailDoc.doc_id}` ? (
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 flex-shrink-0" />
                )}
                Download Office Action
              </Button>
            ) : null}

            {/* Claims download — uploaded or from prosecution history */}
            {(detailDoc?.uploaded_claims_id && detailDoc?.uploaded_claims_filename) ? (
              <Button
                variant="outline"
                className="w-full gap-2 justify-start"
                onClick={() => handleDownloadUploadedDoc(detailDoc.uploaded_claims_id, detailDoc.uploaded_claims_filename, 'Claims')}
                disabled={downloadingProsDoc === `uploaded-${detailDoc.uploaded_claims_id}`}
              >
                {downloadingProsDoc === `uploaded-${detailDoc.uploaded_claims_id}` ? (
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 flex-shrink-0" />
                )}
                Download Claims
              </Button>
            ) : detailDoc?.app_no && detailDoc?.claims_doc_id ? (
              <Button
                variant="outline"
                className="w-full gap-2 justify-start"
                onClick={() => handleDownloadProsDoc(detailDoc.app_no, detailDoc.claims_doc_id, 'Claims')}
                disabled={downloadingProsDoc === `${detailDoc.app_no}-${detailDoc.claims_doc_id}`}
              >
                {downloadingProsDoc === `${detailDoc.app_no}-${detailDoc.claims_doc_id}` ? (
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 flex-shrink-0" />
                )}
                Download Claims
              </Button>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={() => detailDoc && handleDeleteFilled(detailDoc)}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
